import { ReactElement, memo, useMemo } from "react";
import { ArtistContentCardProps } from "../../types";
import { ArtistGridCard } from "./ArtistGridCard";
import { ArtistListCard } from "./ArtistListCard";
import { ArtistHorizontalCard } from "./ArtistHorizontalCard";
import { Link } from "@chakra-ui/react";
import { useSlideInMenu } from "../slideInMenu/hooks/useSlideInMenu";
import { SLIDE_IN_MENUS } from "../../screens/root/constants";
import { trackRecsysCardItemClick } from "../../analytics/recsys/tracking";
import { useGetReaderNavigationContext } from "../../screens/reader/hooks/useGetReaderNavigationContext";
import { applyNavigationAnalyticsAttribution } from "../../analytics/params/navigationContextAttribution";

export const ArtistCard = memo(
  (props: ArtistContentCardProps) => {
    const mode = props.layout.style.mode;
    const { onToggleSlideInMenu } = useSlideInMenu();
    const navigationContext = useGetReaderNavigationContext({
      cardPosition: props.index,
      sectionPosition: props.sectionIndex,
      section: props.section,
    });
    const cards: { [key: string]: ReactElement } = useMemo(
      () => ({
        grid: <ArtistGridCard {...props} />,
        horizontal: <ArtistHorizontalCard {...props} />,
        list: <ArtistListCard {...props} />,
      }),
      [
        props.content,
        props.layout,
        props.fullWidth,
        props.dontOpenInfo,
        props.section,
      ],
    );

    if (cards[mode]) {
      return (
        <Link
          href={props.content.url}
          width={props.fullWidth ? "100%" : "auto"}
          draggable={false}
          onClick={(e) => {
            e.preventDefault();
            const impressionId =
              props.section && props.sectionIndex !== undefined
                ? trackRecsysCardItemClick({
                    section: props.section,
                    sectionIndex: props.sectionIndex,
                    item: props.content,
                    itemIndex: props.trackingIndex ?? props.index ?? 0,
                  })
                : undefined;
            if (props.dontOpenInfo) return;
            onToggleSlideInMenu(SLIDE_IN_MENUS.publisher, {
              artistSlug: props.content.name,
              navigationContext: applyNavigationAnalyticsAttribution(
                navigationContext,
                { impressionId },
              ),
            });
          }}
        >
          {cards[mode]}
        </Link>
      );
    }

    console.warn("Unable to determine layout mode for ArtistCard");
    return null;
  },
  (prevProps, nextProps) => {
    // Only re-render if primitive values or IDs change
    return (
      prevProps.content.id === nextProps.content.id &&
      prevProps.content.user_is_following ===
        nextProps.content.user_is_following &&
      prevProps.layout.style.mode === nextProps.layout.style.mode &&
      prevProps.layout.style.size === nextProps.layout.style.size &&
      prevProps.layout.display === nextProps.layout.display &&
      prevProps.section?.source_type === nextProps.section?.source_type &&
      prevProps.dontOpenInfo === nextProps.dontOpenInfo
    );
  },
);

ArtistCard.displayName = "ArtistCard";
